/**
 * ONE home for the file-transfer lifecycle endpoints' RESPONSE types + their validating parsers.
 *
 * Before this module each of the three surfaces (`apps/web/src/api/client.ts`,
 * `apps/extension/src/background/api.ts`, `apps/cli/src/filetransfer/api.ts`) re-declared the same
 * three interfaces and hand-rolled its own `typeof data.x !== "number"` ladder — three copies that
 * had already drifted in two ways: web/extension returned `data as T` (the raw parsed body, cast
 * whole, extra fields and all) while the CLI rebuilt a narrowed object; and NONE of them validated
 * `partsUploaded`'s ELEMENTS, so a `partsUploaded: ["3"]` body reached the resume reconciler as
 * strings. The request side stays per-surface (each app has its own token/base-URL/fetch plumbing);
 * only parsing is shared — the same split `errors.ts` already uses for `TransferApiError`.
 *
 * BEHAVIOR: a malformed 2xx body now throws `TransferApiError` — the file-transfer error taxonomy
 * every §5-§7 resume branch already dispatches on — instead of a bare `Error` (or, for a
 * non-JSON body, a raw `SyntaxError` escaping the wrapper). `status` is preserved from the real
 * response and `code` is [MALFORMED_RESPONSE_CODE], so no named discriminant changes meaning:
 * `isNetworkError` (status 0) stays false, `isDefinitive404`/`isAlreadyComplete`/`isQuotaExceeded`
 * key off codes this never produces, and the failure lands in the generic `INTERNAL` bucket
 * exactly where an unrecognised create/status failure already lands.
 *
 * SECURITY: parse failures carry only the endpoint label + which field was wrong. Never the body.
 */

import { TransferApiError } from "./errors";

/** `TransferApiError.code` for a 2xx response whose body doesn't match its endpoint's contract. */
export const MALFORMED_RESPONSE_CODE = "malformed_response";

/** `POST /v1/transfers` — 201 with the transfer's server-assigned expiry. */
export interface CreateTransferResponse {
  expiresAt: number;
}

/** `GET /v1/transfers/:id` — the resume algorithm's authoritative reconciliation source (docs/file-transfer-resume.md §5.2). */
export interface TransferStatusResponse {
  status: string;
  partCount: number;
  /** Ascending uploaded-part indexes — the server SET, authoritative over any local cache (§5.2 step 3). */
  partsUploaded: number[];
  cipherBytes: number;
  expiresAt: number;
}

/** `GET /v1/transfers/:id/parts/:n/{upload,download}-url` — one presigned R2 URL + its expiry. */
export interface TransferUploadUrlResponse {
  url: string;
  expiresAt: number;
}

function malformed(endpoint: string, status: number, detail: string): never {
  throw new TransferApiError(
    status,
    MALFORMED_RESPONSE_CODE,
    `${endpoint}: unexpected response shape (${detail})`
  );
}

function asRecord(body: unknown, endpoint: string, status: number): Record<string, unknown> {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    malformed(endpoint, status, "body is not a JSON object");
  }
  return body as Record<string, unknown>;
}

function requireNumber(
  record: Record<string, unknown>,
  field: string,
  endpoint: string,
  status: number
): number {
  const value = record[field];
  // `Number.isFinite` (not a bare `typeof`) — JSON can carry no NaN/Infinity literal, but a
  // proxy/gateway that rewrites a body could, and every consumer here does arithmetic or a
  // deadline comparison with the result.
  if (typeof value !== "number" || !Number.isFinite(value)) {
    malformed(endpoint, status, `${field} is not a finite number`);
  }
  return value;
}

function requireString(
  record: Record<string, unknown>,
  field: string,
  endpoint: string,
  status: number
): string {
  const value = record[field];
  if (typeof value !== "string") malformed(endpoint, status, `${field} is not a string`);
  return value;
}

/** POST /v1/transfers */
export function parseCreateTransferResponse(
  body: unknown,
  status = 200,
  endpoint = "POST /transfers"
): CreateTransferResponse {
  const record = asRecord(body, endpoint, status);
  return { expiresAt: requireNumber(record, "expiresAt", endpoint, status) };
}

/** GET /v1/transfers/:id */
export function parseTransferStatusResponse(
  body: unknown,
  status = 200,
  endpoint = "GET /transfers/:id"
): TransferStatusResponse {
  const record = asRecord(body, endpoint, status);
  const rawParts = record.partsUploaded;
  if (!Array.isArray(rawParts)) malformed(endpoint, status, "partsUploaded is not an array");
  // Element-level validation — the gap every surface's hand-rolled `Array.isArray` check left
  // open. The resume reconciler treats these as part INDEXES (set membership + `Math.max`), so a
  // stringly-typed element silently reconciles to the wrong resume point rather than failing.
  const partsUploaded = rawParts.map((entry, i) => {
    if (typeof entry !== "number" || !Number.isInteger(entry) || entry < 0) {
      malformed(endpoint, status, `partsUploaded[${i}] is not a non-negative integer`);
    }
    return entry;
  });
  return {
    status: requireString(record, "status", endpoint, status),
    partCount: requireNumber(record, "partCount", endpoint, status),
    partsUploaded,
    cipherBytes: requireNumber(record, "cipherBytes", endpoint, status),
    expiresAt: requireNumber(record, "expiresAt", endpoint, status),
  };
}

/** GET /v1/transfers/:id/parts/:n/upload-url and .../download-url (identical shape). */
export function parseTransferUrlResponse(
  body: unknown,
  status = 200,
  endpoint = "GET .../upload-url"
): TransferUploadUrlResponse {
  const record = asRecord(body, endpoint, status);
  return {
    url: requireString(record, "url", endpoint, status),
    expiresAt: requireNumber(record, "expiresAt", endpoint, status),
  };
}

/**
 * Read a 2xx transfer response's JSON body without letting a non-JSON/empty body escape as a raw
 * `SyntaxError`. Returns `undefined` on a parse failure, which every parser above then reports as
 * a `TransferApiError` — the taxonomy, not a stray platform error.
 *
 * Structurally typed (not `Response`) so it accepts both DOM's `json(): Promise<any>` and Node's
 * `json(): Promise<unknown>` — the CLI compiles without the DOM lib.
 */
export async function readTransferJson(res: { json(): Promise<unknown> }): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}
